import React, {useMemo} from "react";

import {useMutation, useQueryClient} from "@tanstack/react-query";
import classNames from "classnames";
import produce from "immer";

import {cancelOrder} from "@globals/DailyOrders/DailyOrdersService";
import {
  OrderDishItem,
  OrderResponseData,
  OrderStatus,
  UPCOMING_ORDERS,
} from "@globals/DailyOrders/interfaces";
import {SECONDARY_ERROR_MODAL_CONTENT} from "@globals/constants/error_modal";
import {useUser} from "@globals/user";

import {DishDetailsModalTrigger} from "@components/DishDetailsModal";
import NomiconIcon from "@components/NomiconIcon";
import TransparentButton from "@components/TransparentButton";
import {CancelOrderModalTrigger} from "@components/modals";
import ErrorModal from "@components/modals/ErrorModal";

import styles from "./DateSelectorItem.scss";

export type DateItemOrdersProps = {
  orders: OrderResponseData[];
};

export default function DateItemOrders({
  orders,
}: DateItemOrdersProps): JSX.Element {
  const queryClient = useQueryClient();
  const isCancelled = orders[0].status === OrderStatus.Cancelled;
  const {user} = useUser();

  const cancelOrderMutation = useMutation<unknown, Error, number>({
    mutationFn: cancelOrder,
    onSuccess: (_resp, orderId) => {
      queryClient.setQueryData(
        [UPCOMING_ORDERS, false],
        (oldData: OrderResponseData[]) => {
          return produce(oldData, (draft) => {
            const cancelledOrder = draft.find((order) => order.id === orderId);
            if (cancelledOrder) {
              cancelledOrder.status = OrderStatus.Cancelled;
            }
          });
        }
      );
    },
  });

  const handleItemsClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    window.location.assign("/orders/upcoming");
  };

  const totalItems = orders.reduce((start, order) => {
    return start + order.order_items.length;
  }, 0);

  const firstOrderItem: OrderDishItem = useMemo(
    () => orders[0].order_items[0],
    [orders]
  );

  const userLocationCount = user?.locations_list?.length ?? 0;

  return (
    <div className={styles.orders}>
      {totalItems > 1 ? (
        <TransparentButton
          aria-label="View upcoming orders"
          onClick={handleItemsClick}
          className={styles.ordersLink}
        >
          {totalItems} items
        </TransparentButton>
      ) : (
        <div className={styles.rows}>
          <div
            className={classNames(
              styles.dishContainer,
              isCancelled && styles.cancelled
            )}
          >
            <DishDetailsModalTrigger
              dish={firstOrderItem.item}
              title={firstOrderItem.item.item}
              hidePurchaseBtnOverride
            >
              <TransparentButton
                aria-label="View dish"
                className={styles.dishName}
              >
                {firstOrderItem.display_name}
              </TransparentButton>
            </DishDetailsModalTrigger>
            <CancelOrderModalTrigger
              order={orders[0]}
              onConfirm={() => {
                cancelOrderMutation.mutate(orders[0].id);
              }}
            >
              <TransparentButton
                aria-label="Cancel order"
                className={styles.cancelOrderButton}
              >
                <NomiconIcon icon="nomicon-dismiss" />
              </TransparentButton>
            </CancelOrderModalTrigger>
            <ErrorModal
              error={cancelOrderMutation?.error}
              defaultContent={SECONDARY_ERROR_MODAL_CONTENT}
            />
          </div>
          <div className={styles.orderLocation}>
            {userLocationCount > 1
              ? orders[0].location.name ?? orders[0].location.address
              : null}
          </div>
        </div>
      )}
    </div>
  );
}
